/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DomainCard.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckBoxBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";

import { useEffect, useMemo, useState, useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import CustomSelectV3 from "../../../components/customComponentsV2/common/select/CustomSelectV3";
import { HandleApiActions } from "../../../redux/actions";
import policiesActions from "../../../redux/actions/policiesActions";
import customConsole from "../../../config/customConsole";

const DomainCard = ({
  domain,
  onChange,
  index,
  handlOnDelete,
  selectedValues = {}, // ✅ object { domain: [] }
  setSelectedValues,
  domainList,
  setSelectedDomainsAllList,
}) => {
  const dispatch = useDispatch();

  const domains = useSelector(
    (state) => state.policyReducer.domains,
    shallowEqual,
  );

  const [actionsByDomain, setActionsByDomain] = useState({});
  const [isActionAllowedOpen, setIsActionAllowedOpen] = useState(true);

  const fetchActionsForDomain = useCallback(
    (domainName) => {
      if (!domainName || actionsByDomain[domainName]) return;

      dispatch(
        HandleApiActions({
          ...policiesActions.get_policies_key_actions,
          params: { domain: domainName },
        }),
      )
        .then((res) => {
          setActionsByDomain((prev) => ({
            ...prev,
            [domainName]: res?.data?.actions ?? [],
          }));
        })
        .catch((err) => {
          customConsole.error("Error fetching actions for domain:", err);
        });
    },
    [actionsByDomain, dispatch],
  );

  useEffect(() => {
    if (domain) fetchActionsForDomain(domain);
  }, [domain, fetchActionsForDomain]);

  const DOMAIN_OPTIONS = useMemo(() => {
    return domains?.data?.domains ?? [];
  }, [domains]);

  const selectedDomains = useMemo(() => {
    return domainList?.map((item) => item.domain) ?? [];
  }, [domainList]);

  useEffect(() => {
    setSelectedDomainsAllList(
      DOMAIN_OPTIONS.filter((d) => !selectedDomains.includes(d)).map((d) => ({
        label: d,
        value: d,
      })),
    );
  }, [DOMAIN_OPTIONS, selectedDomains, setSelectedDomainsAllList]);

  const optionsList = useMemo(() => {
    return actionsByDomain[domain]?.map((item) => item?.canonical) ?? [];
  }, [domain, actionsByDomain]);

  const domainSelectedValues = useMemo(
    () => selectedValues?.[domain] || [],
    [selectedValues, domain],
  );

  const allSelected = useMemo(() => {
    return (
      optionsList.length > 0 &&
      domainSelectedValues.length === optionsList.length
    );
  }, [domainSelectedValues, optionsList]);

  const isIndeterminate = useMemo(() => {
    return (
      domainSelectedValues.length > 0 &&
      domainSelectedValues.length < optionsList.length
    );
  }, [domainSelectedValues, optionsList]);

  const toggleOption = (option) => {
    setSelectedValues((prev) => {
      const current = prev?.[domain] || [];
      const exists = current.includes(option);

      const updated = exists
        ? current.filter((item) => item !== option)
        : [...current, option];

      return {
        ...prev,
        [domain]: updated,
      };
    });
  };

  const handleSelectAll = () => {
    setSelectedValues((prev) => {
      const current = prev?.[domain] || [];

      const updated =
        current.length === optionsList.length ? [] : [...optionsList];

      return {
        ...prev,
        [domain]: updated,
      };
    });
  };

  return (
    <Box
      sx={{
        p: 1,
        boxShadow: 1,
        backgroundColor: "#f1f3f1ff",
        mb: 1,
        borderRadius: 1,
      }}
    >
      {domain ? (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontWeight: 600 }}>{domain}</Typography>

            <IconButton onClick={handlOnDelete}>
              <DeleteIcon sx={{ color: "black" }} />
            </IconButton>
          </Box>

          <Box sx={{ mt: -1 }}>
            <Box
              sx={{
                display: "flex",
                cursor: "pointer",
                alignItems: "center",
              }}
              onClick={() => setIsActionAllowedOpen((prev) => !prev)}
            >
              <Typography fontSize="12px" fontWeight={600}>
                Action Allowed
              </Typography>

              {isActionAllowedOpen ? (
                <ExpandLess fontSize="small" />
              ) : (
                <ExpandMore fontSize="small" />
              )}
            </Box>

            <Collapse in={isActionAllowedOpen} timeout="auto" unmountOnExit>
              <Box sx={{ mt: 1 }}>
                <MenuItem
                  disableRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectAll();
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={isIndeterminate}
                    size="small"
                    disableRipple
                    icon={<CheckBoxBlankIcon sx={{ color: "#26890D" }} />}
                    checkedIcon={<CheckBoxIcon sx={{ color: "#26890D" }} />}
                    sx={{
                      p: 0,
                      mt: "2px",
                      "& .MuiSvgIcon-root": { fontSize: 22, color: "#26890D" },
                    }}
                  />
                  <ListItemText
                    primary="Select All"
                    sx={{
                      m: 0,
                      "& .MuiTypography-root": {
                        fontWeight: 600,
                        fontSize: 14,
                      },
                    }}
                  />
                </MenuItem>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: 1,
                  }}
                >
                  {optionsList.map((option, idx) => {
                    const isChecked =
                      domainSelectedValues.includes(option) || false;

                    return (
                      <MenuItem
                        key={`${option}-${idx}`}
                        disableRipple
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOption(option);
                        }}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          whiteSpace: "normal",
                          borderRadius: 1,
                          "&:hover": { backgroundColor: "#f5f5f5" },
                        }}
                      >
                        <Checkbox
                          checked={isChecked}
                          size="small"
                          disableRipple
                          icon={<CheckBoxBlankIcon sx={{ color: "black" }} />}
                          checkedIcon={
                            <CheckBoxIcon sx={{ color: "#26890D" }} />
                          }
                          sx={{
                            p: 0,
                            mt: "2px",
                            "& .MuiSvgIcon-root": { fontSize: 22 },
                          }}
                        />

                        <ListItemText
                          primary={option}
                          sx={{
                            m: 0,
                            "& .MuiTypography-root": {
                              fontSize: 14,
                              lineHeight: 1.3,
                              mt: 0.5,
                              wordBreak: "break-word",
                            },
                          }}
                        />
                      </MenuItem>
                    );
                  })}
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Box>
      ) : (
        <>
          <Typography sx={{ fontWeight: 500, mb: 1 }}>
            Select Domain #{index + 1}
          </Typography>

          <CustomSelectV3
            size="small"
            label="Domain"
            placeholder="Select Domain"
            required
            selectedValue={domain}
            handleOnChange={(e) => onChange(e.target.value)}
            optionsList={DOMAIN_OPTIONS.filter(
              (d) => !selectedDomains.includes(d),
            ).map((d) => ({
              label: d,
              value: d,
            }))}
          />
        </>
      )}
    </Box>
  );
};

DomainCard.propTypes = {
  domain: PropTypes.string,
  onChange: PropTypes.func,
  index: PropTypes.number,
  handlOnDelete: PropTypes.func,
  selectedValues: PropTypes.object,
  setSelectedValues: PropTypes.func,
  domainList: PropTypes.arrayOf(
    PropTypes.shape({
      domain: PropTypes.string,
    }),
  ),
  setSelectedDomainsAllList: PropTypes.func,
};

export default DomainCard;
